import React, { useState, useEffect } from 'react';
import { X, Filter, Check, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';
import { Input } from '../ui/Input';

const TimezoneFilterModal = ({ 
  isOpen, 
  onClose, 
  onApply, 
  selectedTimezones = [],
  timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00' },
    { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00' },
    { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)', offset: '-07:00' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)', offset: '+00:00' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)', offset: '+01:00' },
    { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', offset: '+04:00' },
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST)', offset: '+05:30' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', offset: '+09:00' },
    { value: 'Asia/Shanghai', label: 'China Standard Time (CST)', offset: '+08:00' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)', offset: '+10:00' },
  ]
}) => {
  const [localSelected, setLocalSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLocalSelected([...selectedTimezones]);
      setSearchQuery('');
    }
  }, [isOpen, selectedTimezones]);

  if (!isOpen) return null;

  const filteredTimezones = timezones.filter(tz =>
    tz.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tz.value?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tz.offset?.includes(searchQuery)
  );

  const handleToggle = (tzValue) => {
    setLocalSelected(prev => {
      if (prev.includes(tzValue)) {
        return prev.filter(t => t !== tzValue);
      } else {
        return [...prev, tzValue];
      }
    });
  };

  const handleSelectAll = () => {
    if (localSelected.length === filteredTimezones.length) {
      setLocalSelected([]);
    } else {
      setLocalSelected(filteredTimezones.map(t => t.value));
    }
  };

  const handleClearAll = () => {
    setLocalSelected([]);
  };

  const handleApply = () => {
    onApply(localSelected);
    onClose();
  };

  const handleReset = () => {
    setLocalSelected([...selectedTimezones]);
  };

  const allSelected = localSelected.length === filteredTimezones.length && filteredTimezones.length > 0;
  const noneSelected = localSelected.length === 0;

  // Group timezones by region
  const groupedTimezones = {
    'Americas': filteredTimezones.filter(tz => tz.value.startsWith('America/')),
    'Europe': filteredTimezones.filter(tz => tz.value.startsWith('Europe/')),
    'Asia': filteredTimezones.filter(tz => tz.value.startsWith('Asia/')),
    'Australia': filteredTimezones.filter(tz => tz.value.startsWith('Australia/')),
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Filter by Timezone
              </h2>
              <p className="text-sm text-gray-500">
                Select timezones to filter
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 flex-1 overflow-y-auto">
          {/* Search */}
          <div className="relative">
            <Input
              placeholder="Search timezones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Filter className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="text-sm"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="text-sm text-red-600 hover:text-red-700"
              disabled={noneSelected}
            >
              Clear All
            </Button>
          </div>

          {/* Timezone Options */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {filteredTimezones.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No timezones found</p>
              </div>
            ) : (
              Object.entries(groupedTimezones).map(([region, tzs]) => 
                tzs.length > 0 && (
                  <div key={region}>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">{region}</p>
                    <div className="space-y-2">
                      {tzs.map((tz) => {
                        const isSelected = localSelected.includes(tz.value);
                        
                        return (
                          <div
                            key={tz.value}
                            className={`
                              flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all
                              ${isSelected 
                                ? 'border-blue-500 bg-blue-50' 
                                : 'border-gray-200 hover:border-gray-300'
                              }
                            `}
                            onClick={() => handleToggle(tz.value)}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggle(tz.value)}
                                className="pointer-events-none"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{tz.label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {tz.value} • UTC{tz.offset}
                                </p>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="ml-2">
                                <Check className="h-5 w-5 text-blue-600" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              )
            )}
          </div>

          {/* Selected Count */}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              {localSelected.length === 0 ? (
                'No timezones selected'
              ) : localSelected.length === filteredTimezones.length ? (
                'All timezones selected'
              ) : (
                `${localSelected.length} of ${filteredTimezones.length} timezones selected`
              )}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={JSON.stringify(localSelected) === JSON.stringify(selectedTimezones)}
          >
            Reset
          </Button>
          <Button
            onClick={handleApply}
            disabled={localSelected.length === 0}
          >
            Apply Filter
            {localSelected.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                {localSelected.length}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TimezoneFilterModal;

